import supabase from "@/services/supabaseClient";

export const ADMIN_FEEDBACK_READ_EVENT = "afrimed:admin-feedback-read";

function cleanText(value) {
  return String(value || "").trim();
}

function normalizeFeedbackType(value) {
  const normalized = cleanText(value).toLowerCase();
  if (normalized === "urgent_issue") return "urgent_issue";
  if (normalized === "issue") return "issue";
  return "suggestion";
}

export function extractFeedbackIdFromNotification(notification) {
  const groupKey = cleanText(notification?.group_key);
  if (groupKey.startsWith("context_feedback:")) {
    return cleanText(groupKey.slice("context_feedback:".length));
  }
  if (groupKey.startsWith("feedback_report:")) {
    return cleanText(groupKey.slice("feedback_report:".length));
  }
  return "";
}

export function notifyAdminFeedbackRead() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(ADMIN_FEEDBACK_READ_EVENT));
}

export function normalizeFeedbackLifecycleStatus(value) {
  const normalized = cleanText(value).toLowerCase();
  if (normalized === "in_review" || normalized === "in review") return "in_review";
  if (normalized === "closed") return "closed";
  if (normalized === "opened" || normalized === "read" || normalized === "reviewed") return "opened";
  return "new";
}

export async function countUnreadFeedbackReports() {
  const { count, error } = await supabase
    .from("v_admin_feedback_reports")
    .select("id", { count: "exact", head: true })
    .eq("status", "new");

  if (error) throw error;
  return Number(count || 0);
}

export async function listAdminHeaderNotifications({
  userId = "",
  limit = 50,
} = {}) {
  const adminUserId = cleanText(userId);
  const requestedLimit = Math.max(Number(limit || 50), 1);
  if (!adminUserId) return [];

  const { data, error } = await supabase
    .from("notifications")
    .select("*")
    .eq("user_id", adminUserId)
    .is("read_at", null)
    .order("created_at", { ascending: false })
    .limit(Math.min(requestedLimit * 3, 150));

  if (error) throw error;

  const notificationRows = Array.isArray(data) ? data : [];
  const feedbackIds = Array.from(
    new Set(
      notificationRows
        .map((row) => extractFeedbackIdFromNotification(row))
        .filter(Boolean),
    ),
  );

  const { data: feedbackRows, error: feedbackError } = await supabase
    .from("v_admin_feedback_reports")
    .select("id,status,feedback_type,message,route,section,created_at,updated_at")
    .eq("status", "new")
    .order("created_at", { ascending: false })
    .limit(Math.min(requestedLimit, 50));

  if (feedbackError) throw feedbackError;

  const newFeedbackRows = Array.isArray(feedbackRows) ? feedbackRows : [];
  const newFeedbackIds = new Set(newFeedbackRows.map((row) => cleanText(row?.id)).filter(Boolean));
  const notificationByFeedbackId = new Map();
  notificationRows.forEach((row) => {
    const feedbackId = extractFeedbackIdFromNotification(row);
    if (feedbackId && newFeedbackIds.has(feedbackId) && !notificationByFeedbackId.has(feedbackId)) {
      notificationByFeedbackId.set(feedbackId, row);
    }
  });

  return newFeedbackRows
    .map((row) => {
      const feedbackId = cleanText(row?.id);
      const existing = notificationByFeedbackId.get(feedbackId);
      if (existing) return existing;

      return {
        id: `feedback:${feedbackId}`,
        title: buildFeedbackNotificationLabel(row?.feedback_type, (_key, fallback) => fallback),
        message:
          cleanText(row?.message) ||
          cleanText(row?.route) ||
          cleanText(row?.section) ||
          "New feedback report",
        type: `admin_feedback:${feedbackId}`,
        priority: "important",
        source: "system",
        audience: "admin",
        group_key: `feedback_report:${feedbackId}`,
        route: "/admin/feedback",
        read_at: null,
        created_at: row?.created_at,
        updated_at: row?.updated_at || row?.created_at,
      };
    })
    .slice(0, requestedLimit);
}

export async function listFeedbackReports(limit = 120) {
  const { data, error } = await supabase
    .from("v_admin_feedback_reports")
    .select("*")
    .order("status_rank", { ascending: true })
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) throw error;
  const rows = Array.isArray(data) ? data : [];
  if (!rows.length) return [];

  const idsBySource = rows.reduce((acc, row) => {
    const source = cleanText(row?.source_table);
    const id = cleanText(row?.id);
    if (!source || !id) return acc;
    if (!acc[source]) acc[source] = [];
    acc[source].push(id);
    return acc;
  }, {});

  const links = [];
  for (const [source, ids] of Object.entries(idsBySource)) {
    const { data: linkRows, error: linkError } = await supabase
      .from("support_ticket_feedback_links")
      .select("feedback_source_table,feedback_id,ticket_id,support_tickets(id,ticket_number,status)")
      .eq("feedback_source_table", source)
      .in("feedback_id", ids);
    if (linkError) throw linkError;
    links.push(...(Array.isArray(linkRows) ? linkRows : []));
  }

  const linkByKey = new Map(
    links.map((link) => [
      `${cleanText(link?.feedback_source_table)}:${cleanText(link?.feedback_id)}`,
      link,
    ]),
  );

  return rows.map((row) => {
    const link = linkByKey.get(`${cleanText(row?.source_table)}:${cleanText(row?.id)}`);
    const ticket = link?.support_tickets || {};
    return {
      ...row,
      support_ticket_id: cleanText(link?.ticket_id || ticket?.id),
      support_ticket_number: cleanText(ticket?.ticket_number),
      support_ticket_status: cleanText(ticket?.status),
    };
  });
}

export async function markFeedbackReportsRead({
  ids = [],
  userId = "",
} = {}) {
  const feedbackIds = Array.from(
    new Set(
      (Array.isArray(ids) ? ids : [])
        .map((value) => cleanText(value))
        .filter(Boolean),
    ),
  );
  const adminUserId = cleanText(userId);

  if (!adminUserId && !feedbackIds.length) return;

  const { error } = await supabase.rpc("mark_admin_feedback_read", {
    feedback_ids: feedbackIds.length ? feedbackIds : null,
  });
  if (error) throw error;

  notifyAdminFeedbackRead();
}

export async function updateFeedbackReportStatus({
  id = "",
  status = "",
} = {}) {
  const feedbackId = cleanText(id);
  const lifecycleStatus = normalizeFeedbackLifecycleStatus(status);
  if (!feedbackId) return;

  const { error } = await supabase.rpc("update_admin_feedback_status", {
    feedback_id: feedbackId,
    next_status: lifecycleStatus,
  });
  if (error) throw error;

  notifyAdminFeedbackRead();
}

export async function assignFeedbackReportToMe(id = "") {
  const feedbackId = cleanText(id);
  if (!feedbackId) return;

  const { error } = await supabase.rpc("update_admin_feedback_status", {
    feedback_id: feedbackId,
    next_status: "in_review",
  });
  if (error) throw error;

  notifyAdminFeedbackRead();
}

export function buildFeedbackNotificationLabel(feedbackType, t) {
  const type = normalizeFeedbackType(feedbackType);
  if (type === "urgent_issue") {
    return t("admin.feedback.type.urgentIssue", "Urgent issue");
  }
  if (type === "issue") {
    return t("admin.feedback.type.issue", "Issue");
  }
  return t("admin.feedback.type.suggestion", "Suggestion");
}
